import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SeasonalThemeDecor from "./SeasonalThemeDecor";
import "./SeasonalOverlay.scss";
import "./Test.scss";

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

function particleKind(themeId, roll) {
  if (themeId === "winter") return roll <= 1 ? "snow" : "flake";
  if (themeId === "halloween") {
    if (roll <= 2) return "bat";
    if (roll === 3) return "ghost";
    return "pumpkin";
  }
  if (themeId === "thanksgiving") {
    if (roll === 0) return "maple";
    if (roll === 1) return "wheat";
    if (roll === 2) return "gourd";
    if (roll === 3) return "corn";
    return "berry";
  }
  if (themeId === "summer") {
    if (roll <= 1) return "bubble";
    if (roll === 2) return "shell";
    if (roll === 3) return "sunray";
    return "fish";
  }
  if (themeId === "albaniaFlag") {
    if (roll <= 2) return "starRed";
    return "confettiBlack";
  }
  return "leaf";
}

function particleSize(kind) {
  if (kind === "bat") return randomInt(48, 72);
  if (kind === "pumpkin") return randomInt(40, 58);
  if (kind === "ghost") return randomInt(40, 56);
  if (kind === "snow") return randomInt(3, 7);
  if (kind === "flake") return randomInt(8, 16);
  if (kind === "maple") return randomInt(26, 40);
  if (kind === "wheat") return randomInt(28, 44);
  if (kind === "gourd") return randomInt(28, 42);
  if (kind === "corn") return randomInt(26, 40);
  if (kind === "berry") return randomInt(12, 18);
  if (kind === "bubble") return randomInt(10, 22);
  if (kind === "shell") return randomInt(18, 28);
  if (kind === "sunray") return randomInt(14, 22);
  if (kind === "fish") return randomInt(20, 32);
  if (kind === "starRed") return randomInt(10, 18);
  if (kind === "confettiBlack") return randomInt(6, 12);
  return randomInt(14, 36);
}

function buildParticles(themeId, count) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const kind = particleKind(themeId, randomInt(0, 4));
    out.push({
      id: `${themeId}-${i}`,
      kind,
      left: randomInt(0, 98),
      duration: randomInt(9, 26),
      delay: randomInt(0, 10),
      size: particleSize(kind),
      sway: randomInt(12, 48),
      spin: randomInt(0, 1) ? 1 : -1,
    });
  }
  return out;
}

function Particle({ p, popped, onPop }) {
  if (popped) {
    return (
      <span
        className="seasonal-pop"
        style={{ left: `${p.left}%`, top: "40%" }}
        aria-hidden
      />
    );
  }

  return (
    <button
      type="button"
      className={`seasonal-particle seasonal-particle--${p.kind}`}
      style={{
        left: `${p.left}%`,
        width: p.size,
        height: p.size,
        "--fall-duration": `${p.duration}s`,
        "--fall-delay": `${p.delay}s`,
        "--sway": `${p.sway}px`,
        "--spin-dir": p.spin,
      }}
      aria-label={`Interactive ${p.kind}`}
      onClick={(e) => {
        e.stopPropagation();
        onPop(p.id, e);
      }}
    />
  );
}

function ChristmasLights({ interactive, onBulbClick, lit }) {
  return (
    <>
      {["up", "down"].map((side) => (
        <ul
          key={side}
          className={`lightrope ${side} ${interactive ? "lightrope--interactive" : ""}`}
          aria-hidden={!interactive}
        >
          {Array.from({ length: 36 }, (_, i) => (
            <li key={i} className={lit[`${side}-${i}`] === false ? "is-off" : ""}>
              {interactive ? (
                <button
                  type="button"
                  className="lightrope__hit"
                  aria-label="Toggle Christmas light"
                  onClick={() => onBulbClick(`${side}-${i}`)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ))}
    </>
  );
}

function defaultParticleCount(themeId, reducedMotion) {
  if (reducedMotion) return 14;
  if (themeId === "thanksgiving" || themeId === "summer") return 52;
  if (themeId === "albaniaFlag") return 40;
  if (themeId === "winter") return 48;
  return 36;
}

function sparkHue(themeId) {
  if (themeId === "thanksgiving") {
    return randomInt(0, 1) ? randomInt(0, 25) : randomInt(35, 48);
  }
  if (themeId === "summer") return randomInt(180, 210);
  if (themeId === "albaniaFlag") return randomInt(0, 1) ? randomInt(0, 8) : 0;
  return randomInt(10, 50);
}

/**
 * Interactive seasonal overlay.
 * @param {"summer"|"halloween"|"autumn"|"thanksgiving"|"albaniaFlag"|"winter"} themeId
 */
function SeasonalOverlay({ themeId = "autumn", particleCount }) {
  const reducedMotion = usePrefersReducedMotion();
  const count = particleCount ?? defaultParticleCount(themeId, reducedMotion);

  const layerRef = useRef(null);
  const [particles, setParticles] = useState(() =>
    buildParticles(themeId, count),
  );
  const [popped, setPopped] = useState(() => new Set());
  const [sparks, setSparks] = useState([]);
  const [lit, setLit] = useState({});
  const [witchFly, setWitchFly] = useState(false);

  useEffect(() => {
    setParticles(buildParticles(themeId, count));
    setPopped(new Set());
    setSparks([]);
    setLit({});
  }, [themeId, count]);

  useEffect(() => {
    if (reducedMotion) return undefined;
    const el = layerRef.current;
    if (!el) return undefined;
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 18;
      const y = (e.clientY / window.innerHeight - 0.5) * 12;
      el.style.setProperty("--parallax-x", `${x.toFixed(1)}px`);
      el.style.setProperty("--parallax-y", `${y.toFixed(1)}px`);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [reducedMotion, themeId]);

  useEffect(() => {
    if (themeId !== "halloween" || reducedMotion) return undefined;
    const tick = () => {
      setWitchFly(true);
      window.setTimeout(() => setWitchFly(false), 9000);
    };
    const first = window.setTimeout(tick, 4000);
    const id = window.setInterval(tick, 28000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(id);
    };
  }, [themeId, reducedMotion]);

  const spawnSparks = useCallback(
    (clientX, clientY, n = 8) => {
      const id = `spark-${Date.now()}-${Math.random()}`;
      const items = Array.from({ length: n }, (_, i) => ({
        id: `${id}-${i}`,
        x: clientX,
        y: clientY,
        dx: randomInt(-80, 80),
        dy: randomInt(-90, 40),
        hue: sparkHue(themeId),
      }));
      setSparks((prev) => [...prev.slice(-40), ...items]);
      window.setTimeout(() => {
        setSparks((prev) => prev.filter((s) => !s.id.startsWith(id)));
      }, 700);
    },
    [themeId],
  );

  const onPop = useCallback(
    (id, e) => {
      setPopped((prev) => new Set(prev).add(id));
      spawnSparks(e.clientX, e.clientY, 10);
      window.setTimeout(() => {
        setPopped((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, 2200);
    },
    [spawnSparks],
  );

  const onBulbClick = useCallback((key) => {
    setLit((prev) => ({ ...prev, [key]: prev[key] === false }));
  }, []);

  const onAmbientClick = useCallback(
    (e) => spawnSparks(e.clientX, e.clientY, 14),
    [spawnSparks],
  );

  const liveParticles = useMemo(
    () =>
      particles.map((p) => (
        <Particle
          key={p.id}
          p={p}
          popped={popped.has(p.id)}
          onPop={onPop}
        />
      )),
    [particles, popped, onPop],
  );

  return (
    <div
      ref={layerRef}
      className={`seasonal-overlay seasonal-overlay--${themeId}${
        reducedMotion ? " seasonal-overlay--reduced" : ""
      }`}
      data-seasonal-overlay={themeId}
    >
      <SeasonalThemeDecor
        themeId={themeId}
        witchFly={witchFly}
        onAmbientClick={onAmbientClick}
        reducedMotion={reducedMotion}
        lit={lit}
        onBulbClick={onBulbClick}
        ChristmasLights={ChristmasLights}
      />

      <div className="seasonal-overlay__particles">{liveParticles}</div>

      {sparks.map((s) => (
        <span
          key={s.id}
          className="seasonal-spark"
          style={{
            left: s.x,
            top: s.y,
            "--dx": `${s.dx}px`,
            "--dy": `${s.dy}px`,
            "--hue": s.hue,
          }}
        />
      ))}
    </div>
  );
}

export default SeasonalOverlay;
